import axiosInstance from "../../utils/AxiosInstance";
import { toast } from "react-toastify";
import { useAuthHeader } from "react-auth-kit";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useGetPatients } from "../PatientComponents";
import { deviceCategories, devices } from ".";
import { prettify, toastError, toastSuccess } from "../../utils/utils";

// GET /devices
export const useGetDevices = (filter = "all") => {
  const authHeader = useAuthHeader();
  const patientsQuery = useGetPatients();

  return useQuery({
    queryKey: ["devices", filter],
    queryFn: async () => {
      const res = await axiosInstance.get(`/devices`, {
        headers: { Authorization: authHeader() },
      });
      let data = res.data;

      let _devices = [];

      // get all device_types beloging to the "filter" category
      let filteredDeviceTypes = [];
      devices.forEach((el) => {
        if (el.category === filter) filteredDeviceTypes.push(el.device_type);
      });

      // now filter by device_type
      const filteredDevices =
        filter === "all"
          ? [...data.devices]
          : data.devices.filter((el) =>
              filteredDeviceTypes.includes(el.device_type)
            );

      // iterate and set patient and doctor informations for rendering
      for (let d of filteredDevices) {
        let devicePatient = patientsQuery.data.patients.find(
          (el) => el.id === d.patient_id
        );

        let patientInfo =
          devicePatient === undefined
            ? "Non associato"
            : `${devicePatient.first_name} ${devicePatient.last_name}`;

        _devices.push({
          ...d,
          patient_info: patientInfo,
          // device_type_label: `${
          //   devices.find((el) => el.id === d.device_type).category
          // } / ${prettify(devices.find((el) => el.id === d.device_type).label)}`,
        });
      }
      data["devices"] = _devices;
      return data;
    },
    enabled: patientsQuery.isSuccess,
  });
};

// GET /devices/:device_id
export const useGetDeviceById = (device_id) => {
  const authHeader = useAuthHeader();

  return useQuery({
    queryKey: ["devices", device_id],
    queryFn: async () => {
      const res = await axiosInstance.get(`/devices/${device_id}`, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
    enabled: device_id !== undefined && device_id !== null,
  });
};

// GET /devices/patient/:patient_id
export const useGetPatientDevices = (patient_id) => {
  const authHeader = useAuthHeader();

  return useQuery({
    queryKey: ["devices", "patient", patient_id],
    queryFn: async () => {
      const res = await axiosInstance.get(`/devices/patient/${patient_id}`, {
        headers: { Authorization: authHeader() },
      });
      return res.data;
    },
    enabled: patient_id !== undefined && patient_id !== null,
  });
};

// === PUT /devices/:device_id ===
export const usePutDeviceById = (device_id) => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();
  const getOriginalData = useGetDeviceById(device_id);

  return useMutation({
    mutationFn: async (editedDevice) => {
      if (editedDevice === undefined) return;
      const device_id = editedDevice.id;
      const res = await axiosInstance.put(
        `/devices/${device_id}`,
        editedDevice,
        {
          headers: { Authorization: authHeader() },
        }
      );
      return res.data;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["devices"]); // update views inside Doctors page
      queryClient.invalidateQueries(["patients"]); // update views inside Patients page
      queryClient.invalidateQueries(["devices", variables.id]); // update views inside ViewDoctor
      queryClient.invalidateQueries([
        "devices",
        "patient",
        variables.patient_id,
      ]);
      toast.success("Dettagli dispositivo aggiornati!", toastSuccess);
    },
    onError: (data, variables, context) => {
      let newData =
        device_id === null
          ? queryClient.getQueryData(["devices", variables.id])
          : getOriginalData.data;
      queryClient.setQueryData(["devices", device_id], (oldData) => {
        return {
          ...oldData,
          ...newData,
        };
      });
      toast.error("Dettagli dispositivo non aggiornati", toastError);
    },
  });
};

// === PUT /devices/:device_id ===
export const useDissociateFromPatient = () => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (editedDevice) => {
      const device_id = editedDevice.id;
      editedDevice = {
        ...editedDevice,
        patient_id: null,
      };

      const res = await axiosInstance.put(
        `/devices/${device_id}`,
        editedDevice,
        {
          headers: {
            Authorization: authHeader(),
          },
        }
      );
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries(["patients"]); // update views inside ViewDevice
      queryClient.invalidateQueries(["devices"]); // update views inside Doctors page
      queryClient.invalidateQueries(["patients", variables.patient_id]); // update views inside ViewDoctor
      queryClient.invalidateQueries(["devices", variables.device_id]); // update views inside ViewDoctor
      toast.success("Dettagli dispositivo aggiornati!", toastSuccess);
    },
    onError: () => {
      toast.error("Dettagli dispositivo non aggiornati", toastError);
    },
  });
};

// === POST /devices ===
export const useNewDevice = (closeModal) => {
  const authHeader = useAuthHeader();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (newDevice) => {
      const res = await axiosInstance.post(`/devices`, newDevice, {
        headers: {
          Authorization: authHeader(),
        },
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["patients"]); // update views inside Doctors page
      queryClient.invalidateQueries(["devices"]); // update views inside Doctors page
      toast.success("Dispositivo aggiunto con successo!", toastSuccess);
      closeModal();
    },
    onError: () => {
      toast.error("Errore nell'aggiunta del dispositivo", toastError);
      closeModal();
    },
  });
};
